import React, { useState, useEffect } from "react";
import { C, fmt, statusLabel, text } from "../theme";
import { usePermissions } from "../PermissionContext";
import WatcherPanel from "../components/WatcherPanel";
import DocumentSection from "../components/DocumentSection";

import { Card, Btn, Notice, ConfirmDialog, DocLink, StatusPill as Badge, ApprovalTrail, ReturnedBanner, rowAction } from "../components/ui";

export default function InvoicePage({ initialId, onOpenDoc }) {
  const { me, can } = usePermissions();
  const [invoices, setInvoices] = useState([]);
  const [pos, setPOs] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [taxCodes, setTaxCodes] = useState([]);

  // Form state
  const [selectedPO, setSelectedPO] = useState(null);
  const [vendorInvNo, setVendorInvNo] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }
  const [confirmingPaid, setConfirmingPaid] = useState(false);

  const loadList = () => {
    setLoading(true);
    Promise.all([
      fetch("/api/invoices").then(r => r.json()),
      fetch("/api/pos").then(r => r.json()),
    ]).then(([inv, po]) => {
      setInvoices(inv);
      setPOs(po);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(loadList, []);
  // A non-OK response (e.g. 403 for roles without reference:view) must degrade
  // to "no tax codes", never reach state as an error object (it gets .filter'd).
  useEffect(() => { fetch("/api/reference/tax-codes").then(r => (r.ok ? r.json() : [])).then(d => setTaxCodes(Array.isArray(d) ? d : [])).catch(() => setTaxCodes([])); }, []);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  useEffect(() => { fetch("/api/xero/accounts").then(r => r.json()).then(setXeroAccounts).catch(() => setXeroAccounts([])); }, []);

  const loadDetail = (id) => {
    setSel(id);
    fetch("/api/invoices/" + id).then(r => r.json()).then(setDetail).catch(console.error);
  };
  // Deep link from the Approvals card or dashboard: open that invoice on mount.
  useEffect(() => { if (initialId) loadDetail(initialId); }, [initialId]); // eslint-disable-line react-hooks/exhaustive-deps

  // POs that can have invoices (fulfilled or issued with GRN)
  const invoiceablePOs = pos.filter(po => ["ISSUED", "PARTIALLY_RECEIVED", "FULFILLED"].includes(po.status));

  const poDetail = selectedPO ? pos.find(p => p.id === selectedPO) : null;

  const handleSelectPO = (poId) => {
    setSelectedPO(poId);
    const po = pos.find(p => p.id === poId);
    if (po?.lineItems) {
      setLineItems(po.lineItems.map(li => ({
        description: li.description,
        quantity: li.quantity,
        unitPrice: li.unitPrice,
        taxCodeId: li.taxCodeId || null,
        gstRate: li.gstRate || 0,
        accountCode: li.accountCode || null,
      })));
    }
  };

  const updateLine = (idx, field, value) => {
    const lines = [...lineItems];
    lines[idx] = { ...lines[idx], [field]: field === "quantity" || field === "unitPrice" ? Number(value) : value };
    setLineItems(lines);
  };

  const invTotal = lineItems.reduce((s, li) => s + (li.quantity * li.unitPrice), 0);
  const taxById = Object.fromEntries(taxCodes.map(t => [t.id, t]));
  const invGst = Math.round(lineItems.reduce((s, li) => {
    const rate = taxById[li.taxCodeId]?.rate ?? li.gstRate ?? 0;
    return s + Math.round((li.quantity * li.unitPrice * rate / 100 + Number.EPSILON) * 100) / 100;
  }, 0) * 100) / 100;
  const invGross = Math.round((invTotal + invGst) * 100) / 100;

  const handleCreate = async () => {
    if (!selectedPO || !vendorInvNo.trim()) {
      setNotice({ kind: "error", text: "Please select a PO and enter the vendor invoice number." });
      return;
    }
    setSubmitting(true);
    try {
      const po = pos.find(p => p.id === selectedPO);
      const res = await fetch("/api/invoices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          poId: selectedPO,
          vendorInvNumber: vendorInvNo.trim(),
          submittedById: po.requestorId,
          lineItems: lineItems.map(li => ({
            description: li.description,
            quantity: li.quantity,
            unitPrice: li.unitPrice,
            taxCodeId: li.taxCodeId || null,
            gstRate: taxById[li.taxCodeId]?.rate ?? li.gstRate ?? 0,
            accountCode: li.accountCode || null,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // The form stays mounted with its input intact so the user can fix and retry.
        setNotice({ kind: "error", text: "Error: " + (data.error || "Failed to create invoice") });
        return;
      }
      const matchMsg = data.matchResult?.status === "MATCHED" ? "✅ 3-Way Match: PASSED" : "⚠️ 3-Way Match: " + data.matchResult?.status;
      setNotice({ kind: "success", text: "Invoice Created: " + data.invoiceNumber + "\n" + matchMsg });
      setShowForm(false);
      setSelectedPO(null);
      setVendorInvNo("");
      setLineItems([]);
      loadList();
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (id) => {
    const res = await fetch("/api/invoices/" + id + "/submit", { method: "POST" });
    const data = await res.json();
    if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (data.error || "Failed") }); return; }
    setNotice({ kind: "success", text: data.message });
    loadDetail(id);
    loadList();
  };

  const handlePushXero = async (id) => {
    if ((detail?.lineItems || []).some(li => !li.accountCode)) {
      setNotice({ kind: "error", text: "Every line item needs an account code before pushing to Xero." });
      return;
    }
    const res = await fetch("/api/invoices/" + id + "/push-xero", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (data.error || "Push to Xero failed (" + res.status + ")") }); return; }
    setNotice({ kind: "success", text: data.message + (data.xeroBillId ? "\nBill ID: " + data.xeroBillId : "") });
    loadDetail(id);
    loadList();
  };

  const handleMarkPaid = async (id) => {
    setConfirmingPaid(false);
    const res = await fetch("/api/invoices/" + id + "/mark-paid", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: "system" }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (data.error || "Failed to mark paid") }); return; }
    setNotice({ kind: "success", text: "Invoice marked as paid" });
    loadDetail(id);
    loadList();
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  // ─── Create Form ───
  if (showForm) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => { setShowForm(false); setSelectedPO(null); }}>← Cancel</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>New Invoice</h1>
        </div>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 4 }}>Step 1: Select Purchase Order</div>
          <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 14 }}>Link this invoice to a PO for 3-way matching (Invoice vs PO vs GRN).</div>

          {invoiceablePOs.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", background: C.warningBg, borderRadius: 8 }}>
              <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning }}>No POs Available</div>
              <div style={{ fontSize: text.sm, color: C.gray600, marginTop: 6, lineHeight: 1.5 }}>
                Only POs that are issued, partially received, or fulfilled can be invoiced, and you only see POs your
                role's data scope allows. If a PO you expect is missing, check its status on the Purchase Orders page
                or ask an administrator about your role's data scope.
              </div>
            </div>
          ) : (
            invoiceablePOs.map(po => {
              const isSel = selectedPO === po.id;
              return (
                <div key={po.id} onClick={() => handleSelectPO(po.id)} style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", marginBottom: 8,
                  borderRadius: 10, cursor: "pointer",
                  border: isSel ? `2px solid ${C.primary}` : `2px solid ${C.gray200}`,
                  background: isSel ? C.primaryBg : C.white,
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                    border: isSel ? `2px solid ${C.primary}` : `2px solid ${C.gray300}`,
                    background: isSel ? C.primary : C.white,
                    display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: text.sm,
                  }}>{isSel ? "✓" : ""}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: text.lg, fontWeight: 700 }}>{po.poNumber} — {po.vendor}</div>
                    <div style={{ fontSize: text.sm, color: C.gray600 }}>AOR: {po.aor?.aorNumber} • {po.businessUnit}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: text.h3, fontWeight: 700 }}>{fmt(po.total)}</div>
                    <Badge status={po.status} />
                  </div>
                </div>
              );
            })
          )}
        </Card>

        {poDetail && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 14 }}>Step 2: Vendor Invoice Details</div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="inv-vendor-inv-no" style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }}>Vendor Invoice Number *</label>
                <input id="inv-vendor-inv-no" value={vendorInvNo} onChange={e => setVendorInvNo(e.target.value)} placeholder="e.g. AWS-SG-88210, INV-2026-001" style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.lg, fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 4 }}>Step 3: Verify Line Items</div>
              <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 14 }}>Pre-filled from PO. Adjust if the vendor invoice differs — mismatches will be flagged by 3-way matching.</div>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
                <thead><tr>{["Description", "Qty", "Unit Price", "Tax", "Account", "Total", "vs PO"].map((h, i) => (
                  <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {lineItems.map((li, idx) => {
                    const poLine = poDetail.lineItems?.find(pl => pl.description === li.description);
                    const qtyMatch = poLine && li.quantity === poLine.quantity;
                    const priceMatch = poLine && Math.abs(li.unitPrice - poLine.unitPrice) < 1;
                    return (
                      <tr key={idx} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                        <td style={{ padding: "8px 10px" }}>{li.description}</td>
                        <td style={{ padding: "8px 10px", width: 80 }}>
                          <input type="number" min="1" value={li.quantity} onChange={e => updateLine(idx, "quantity", e.target.value)} aria-label={`Line ${idx + 1} qty`}
                            style={{ width: "100%", padding: "6px 8px", border: `1px solid ${qtyMatch ? C.gray300 : C.dangerLight}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", textAlign: "center", boxSizing: "border-box", background: qtyMatch ? C.white : C.dangerBg }} />
                        </td>
                        <td style={{ padding: "8px 10px", width: 120 }}>
                          <input type="number" min="0" step="0.01" value={li.unitPrice} onChange={e => updateLine(idx, "unitPrice", e.target.value)} aria-label={`Line ${idx + 1} unit price`}
                            style={{ width: "100%", padding: "6px 8px", border: `1px solid ${priceMatch ? C.gray300 : C.dangerLight}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", textAlign: "center", boxSizing: "border-box", background: priceMatch ? C.white : C.dangerBg }} />
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <select value={li.taxCodeId || ""} onChange={e => updateLine(idx, "taxCodeId", e.target.value)} aria-label={`Line ${idx + 1} tax`} style={{ padding: "4px 6px", border: `1px solid ${C.gray300}`, borderRadius: 6 }}>
                            <option value="">—</option>
                            {taxCodes.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.code} ({t.rate}%)</option>)}
                          </select>
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <select value={li.accountCode || ""} onChange={e => updateLine(idx, "accountCode", e.target.value)} aria-label={`Line ${idx + 1} account`} style={{ padding: "4px 6px", border: `1px solid ${li.accountCode ? C.gray300 : C.danger}`, borderRadius: 6 }}>
                            <option value="">— account —</option>
                            {xeroAccounts.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: "8px 10px", fontWeight: 700 }}>{fmt(li.quantity * li.unitPrice)}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {qtyMatch && priceMatch ? (
                            <span style={{ color: C.success, fontWeight: 600, fontSize: text.sm }}>✓ Match</span>
                          ) : (
                            <span style={{ color: C.danger, fontWeight: 600, fontSize: text.sm }}>⚠ Mismatch</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
                <div style={{ fontSize: text.sm, color: C.gray500 }}>PO Total (net): {fmt(poDetail.total)}</div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: text.md }}>Net: {fmt(invTotal)} • GST: {fmt(invGst)}</div>
                  <div style={{ fontSize: text.h3, fontWeight: 700, color: invTotal !== poDetail.total ? C.danger : C.gray1100 }}>
                    Gross: {fmt(invGross)} {invTotal !== poDetail.total && <span style={{ fontSize: text.sm, color: C.danger }}>(net differs from PO)</span>}
                  </div>
                </div>
              </div>
            </Card>

            <div style={{ display: "flex", gap: 10 }}>
              <Btn onClick={handleCreate} disabled={submitting || !vendorInvNo.trim()}>
                {submitting ? "Creating..." : "Create Invoice — " + fmt(invTotal)}
              </Btn>
              <Btn variant="outline" onClick={() => { setShowForm(false); setSelectedPO(null); }}>Cancel</Btn>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Detail View ───
  if (sel && detail) {
    const inv = detail;
    const md = inv.matchDetail;
    const canSubmit = inv.status === "DRAFT" || inv.status === "RETURNED";
    // Mirror the server's push-xero gate (invoice:edit) so the button never
    // renders for a role whose POST would 403.
    const canPush = inv.status === "APPROVED" && !inv.xeroStatus && can("invoice", "edit");
    const canPay = inv.xeroStatus === "PUSHED" && inv.paymentStatus !== "PAID";

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => { setSel(null); setDetail(null); }} style={{ padding: "4px 10px", fontSize: text.xs }}>← Back</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{inv.invoiceNumber}</h1>
          <Badge status={inv.status} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {canSubmit && <Btn variant="success" onClick={() => handleSubmit(inv.id)}>Submit for Approval</Btn>}
            {canPush && <Btn variant="purple" onClick={() => handlePushXero(inv.id)}>Push to Xero →</Btn>}
            {canPay && can("invoice", "edit") && <Btn variant="success" onClick={() => setConfirmingPaid(true)}>Mark Paid</Btn>}
          </div>
        </div>

        {/* Mark Paid is a bypass of the payment-run chain — say so before it
            commits (UX-40); the server additionally gates it. */}
        <ConfirmDialog
          open={confirmingPaid}
          title="Mark this invoice as paid?"
          confirmLabel="Mark paid"
          variant="danger"
          onConfirm={() => handleMarkPaid(inv.id)}
          onCancel={() => setConfirmingPaid(false)}
        >
          {`This records that ${inv.invoiceNumber} (${fmt(inv.grossTotal || inv.total)} gross) was settled OUTSIDE the in-system payment run — e.g. paid directly in Xero.\n\nIt immediately disappears from payment-run eligibility and skips the Preparer → Approver → Executor chain. If this invoice should be paid through a payment run, don't mark it — create or wait for the run instead.`}
        </ConfirmDialog>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        <ReturnedBanner status={inv.status} approvals={inv.approvals} />

        {/* The end of the approval chain isn't the end of the pipeline (UX-13):
            an approved invoice still needs the Xero push to become payable. */}
        {inv.status === "APPROVED" && !inv.xeroStatus && (
          <Card style={{ marginBottom: 16, background: C.warningBg, border: `1px solid ${C.warningLight}` }}>
            <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning, marginBottom: 4 }}>Approved — not yet payable</div>
            <div style={{ fontSize: text.sm, color: C.gray700, lineHeight: 1.5 }}>
              Payment runs only pick up invoices that have been pushed to Xero.
              {can("invoice", "edit")
                ? " Use “Push to Xero” above (every line needs an account code first) to make this invoice eligible."
                : " Finance pushes approved invoices to Xero; until then this invoice won't appear in any payment run."}
            </div>
          </Card>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
            <div><span style={{ color: C.gray500 }}>Vendor Invoice:</span> <strong>{inv.vendorInvNumber}</strong></div>
            <div><span style={{ color: C.gray500 }}>Vendor:</span> <strong>{inv.vendor}</strong></div>
            <div><span style={{ color: C.gray500 }}>PO Ref:</span> {onOpenDoc && inv.poId ? <DocLink onClick={() => onOpenDoc("po", inv.poId)}>{inv.po?.poNumber}</DocLink> : <strong style={{ color: C.primary }}>{inv.po?.poNumber}</strong>}</div>
            <div><span style={{ color: C.gray500 }}>Net:</span> <strong>{fmt(inv.total)}</strong></div>
            <div><span style={{ color: C.gray500 }}>GST:</span> <strong>{fmt(inv.gstTotal || 0)}</strong></div>
            <div><span style={{ color: C.gray500 }}>Gross:</span> <strong style={{ fontSize: text.h3 }}>{fmt(inv.grossTotal || inv.total)}</strong></div>
            <div><span style={{ color: C.gray500 }}>Date:</span> <strong>{new Date(inv.createdAt).toLocaleDateString()}</strong></div>
            <div><span style={{ color: C.gray500 }}>Submitted By:</span> <strong>{inv.submittedBy?.displayName}</strong></div>
          </div>
        </Card>

        {md && (
          <Card style={{ padding: 0, marginBottom: 16, border: `2px solid ${md.status === "MATCHED" ? C.successLight : C.dangerLight}`, overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", background: md.status === "MATCHED" ? C.successBg : C.dangerBg, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: text.h3 }}>{md.status === "MATCHED" ? "✅" : "⚠️"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: text.md, fontWeight: 700, color: md.status === "MATCHED" ? C.success : C.danger }}>3-Way Match: {statusLabel(md.status)}</div>
              </div>
              <Badge status={md.status} />
            </div>
            <div style={{ padding: "14px 18px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.sm }}>
                <thead><tr>{["Check", "Status", "Invoice", "PO / GRN", "Diff"].map((h, i) => (
                  <th scope="col" key={i} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `1px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.micro, textTransform: "uppercase" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {md.details?.map((d, i) => {
                    // Qty and GRN rows compare QUANTITIES — rendering them with
                    // fmt() printed "GRN: chair — $10 vs 10" (UX-23). Only the
                    // amount checks are money.
                    const isMoney = /^(Total|Price|Line)/.test(d.check || "");
                    const num = (v) => (typeof v === "number" ? (isMoney ? fmt(v) : String(v)) : v);
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{d.check}</td>
                        <td style={{ padding: "8px 10px" }}>
                          <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: text.micro, fontWeight: 700, background: ["MATCH", "PASSED"].includes(d.status) ? C.successBg : C.dangerBg, color: ["MATCH", "PASSED"].includes(d.status) ? C.success : C.danger }}>{d.status}</span>
                        </td>
                        <td style={{ padding: "8px 10px" }}>{d.invoice != null ? num(d.invoice) : "—"}</td>
                        <td style={{ padding: "8px 10px" }}>{d.po != null ? num(d.po) : d.received != null ? num(d.received) : "—"}</td>
                        <td style={{ padding: "8px 10px", color: d.diff ? C.danger : C.gray400 }}>{d.diff != null ? (d.diff > 0 ? "+" : "") + (isMoney ? fmt(d.diff) : d.diff) : d.shortfall != null ? "-" + d.shortfall : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {(inv.xeroStatus || canPush) && (
          <Card style={{ marginBottom: 16, border: inv.xeroStatus ? `1px solid ${C.purpleBorder}` : `1px solid ${C.gray200}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: text.lg, fontWeight: 700 }}>Xero Integration</div>
                <div style={{ fontSize: text.sm, color: C.gray500 }}>{inv.xeroBillId ? "Bill ID: " + inv.xeroBillId : "Ready to push"}</div>
              </div>
              {inv.xeroStatus && <Badge status="PUSHED_TO_XERO" />}
            </div>
          </Card>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: text.lg, fontWeight: 700 }}>Payment Status</div>
            <Badge status={inv.paymentStatus} />
          </div>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Line Items</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
            <thead><tr>{["Description", "Qty", "Unit Price", "Total"].map((h, i) => (
              <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {inv.lineItems?.map((li, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                  <td style={{ padding: "10px 12px" }}>{li.description}</td>
                  <td style={{ padding: "10px 12px" }}>{li.quantity}</td>
                  <td style={{ padding: "10px 12px" }}>{fmt(li.unitPrice)}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{fmt(li.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <ApprovalTrail approvals={inv.approvals} docType="INVOICE" />

        <Card>
          <DocumentSection
            entityType="invoice"
            entityId={inv.id}
            documents={inv.documents || []}
            onChange={() => loadDetail(inv.id)}
          />
        </Card>

        <WatcherPanel docType="INVOICE" docId={inv.id} canManage={!!me && (me.id === inv.submittedById || me.roleName === "Administrator")} />
      </div>
    );
  }

  // ─── List View ───
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Invoices</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>{invoices.length} records</p>
        </div>
        {can("invoice", "create") && <Btn onClick={() => { setNotice(null); setShowForm(true); }}>+ New Invoice</Btn>}
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        {/* Net AND gross (UX-57): payment-tier banding runs on gross, so the list
            a Preparer scans must show the number the sieve uses. */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Invoice No.", "Date", "Vendor Inv", "Vendor", "PO Ref", "Net", "Gross (banding)", "Match", "Status", "Payment"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} {...rowAction(() => { setNotice(null); loadDetail(inv.id); })} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.gray50}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>{inv.invoiceNumber}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(inv.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px" }}>{inv.vendorInvNumber}</td>
                <td style={{ padding: "10px 12px" }}>{inv.vendor}</td>
                <td style={{ padding: "10px 12px", color: C.primary }}>{inv.po?.poNumber}</td>
                <td style={{ padding: "10px 12px" }}>{fmt(inv.total)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{fmt(inv.grossTotal || inv.total)}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={inv.matchStatus} /></td>
                <td style={{ padding: "10px 12px" }}><Badge status={inv.status} /></td>
                <td style={{ padding: "10px 12px" }}><Badge status={inv.paymentStatus} /></td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 30, textAlign: "center", color: C.gray500 }}>No invoices yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}